/* Presentation only: both controls resolve to the original, unchanged Shopify option. */
if (!customElements.get('kk-size-length')) {
  customElements.define('kk-size-length', class extends HTMLElement {
    connectedCallback() {
      customElements.whenDefined('variant-picker').then(() => {
        if (this.isConnected && !this.initialized) this.init();
      });
    }

    static parse(value) {
      const match = value.trim().match(/^((?:[2-4]?X{0,2}[SML])|\d{2})\s*\/\s*(?:L\s*)?["“”]?\s*(\d{2})\s*["“”]?$/i);
      if (!match || !['26', '28', '30', '32', '34', '36', '38'].includes(match[2])) return null;
      return { size: match[1].toUpperCase(), length: match[2] };
    }

    init() {
      this.picker = this.closest('variant-picker');
      if (!this.picker) return;
      this.inputs = Array.from(this.picker.querySelectorAll('input[type="radio"]'))
        .filter((input) => input.name === this.dataset.optionName);
      if (!this.inputs.length) return;
      this.entries = this.inputs.map((input) => ({ input, ...this.constructor.parse(input.value) }));
      // Mixed or ambiguous sizing retains the original selector.
      if (this.entries.some((entry) => !entry.size)) return;
      const pairs = this.entries.map((entry) => `${entry.size}/${entry.length}`);
      if (new Set(pairs).size !== pairs.length) return;
      this.source = this.inputs[0].closest('.option-selector');
      this.index = Number(this.dataset.optionIndex);
      this.sizes = [...new Set(this.entries.map((entry) => entry.size))];
      if (this.sizes.every((size) => /^\d+$/.test(size))) this.sizes.sort((a, b) => Number(a) - Number(b));
      this.lengths = [...new Set(this.entries.map((entry) => entry.length))].sort((a, b) => Number(a) - Number(b));
      this.controls = [];
      this.append(this.group('Størrelse', 'size', this.sizes), this.group('Vælg længde', 'length', this.lengths));
      const guide = this.source.querySelector('.kk-size-guide-link');
      if (guide) this.append(guide);
      this.sync();
      this.source.classList.add('kk-combined-size-source');
      this.hidden = false;
      this.initialized = true;
      this.addEventListener('change', (event) => {
        event.stopPropagation();
        const control = event.target;
        if (!control.dataset.part) return;
        this[control.dataset.part] = control.value;
        if (control.dataset.part === 'size' && !this.entries.some((entry) =>
          entry.size === this.size && entry.length === this.length && this.available(entry))) {
          this.length = null;
        }
        const entry = this.entries.find((item) => item.size === this.size && item.length === this.length);
        this.inputs.forEach((input) => { input.checked = input === entry?.input; });
        (entry?.input || this.source).dispatchEvent(new Event('change', { bubbles: true }));
        this.refresh();
      });
      this.onVariantChange = () => { this.sync(); this.refresh(); };
      this.picker.addEventListener('on:variant:change', this.onVariantChange);
      this.refresh();
    }

    group(title, part, values) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'kk-size-length-group';
      const legend = document.createElement('legend');
      legend.className = 'label';
      legend.textContent = title;
      const buttons = document.createElement('div');
      buttons.className = 'option-selector__btns flex flex-wrap';
      values.forEach((value, index) => {
        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'opt-btn visually-hidden focus-label';
        input.name = `${this.dataset.optionName}-${part}`;
        input.id = `${input.name}-${index}`;
        input.value = value;
        input.dataset.part = part;
        const label = document.createElement('label');
        label.className = 'opt-label opt-label--btn btn relative text-center';
        label.htmlFor = input.id;
        label.textContent = value;
        buttons.append(input, label);
        this.controls.push({ input, label, part, value });
      });
      fieldset.append(legend, buttons);
      return fieldset;
    }

    sync() {
      const entry = this.entries.find((item) => item.input.checked);
      if (entry) { this.size = entry.size; this.length = entry.length; }
    }

    available(entry) {
      const selected = this.picker.getSelectedOptions();
      return this.picker.data.product.variants.some((variant) => variant.available
        && variant.options[this.index] === entry.input.value
        && variant.options.every((value, index) => index === this.index || !selected[index] || value === selected[index]));
    }

    refresh() {
      this.controls.forEach(({ input, label, part, value }) => {
        const candidates = this.entries.filter((entry) => part === 'size'
          ? entry.size === value : entry.size === this.size && entry.length === value);
        const available = candidates.some((entry) => this.available(entry));
        input.checked = this[part] === value;
        input.disabled = !available;
        input.classList.toggle('is-unavailable', !available);
        label.title = available ? '' : (part === 'length' && !this.size ? 'Vælg størrelse først' : 'Ikke på lager i denne kombination');
      });
    }

    disconnectedCallback() {
      if (this.onVariantChange) this.picker.removeEventListener('on:variant:change', this.onVariantChange);
    }
  });
}
